/**
 * FILE: apps/hub/src/components/ChunkErrorBoundary.tsx
 * PURPOSE: Catches Vite dynamic import chunk load failures (stale cache after deploy).
 * AGENT_CONTEXT: Shows friendly "Please refresh" message instead of white screen.
 * LAST_REVIEWED: 2026-05-20
 */

import { useRouteError } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'

function isChunkError(err: unknown): boolean {
  if (!err) return false
  const msg = String(err)
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk')
  )
}

export function ChunkErrorBoundary() {
  const error = useRouteError()

  if (isChunkError(error)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-fintree-navy px-4 text-center">
        <div className="max-w-md space-y-6">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-card/10">
              <RefreshCw className="h-8 w-8 text-fintree-gold" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">Update Available</h1>
          <p className="text-muted-foreground">
            The app has been updated. Please refresh the page to load the latest version.
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-primary text-white hover:bg-fintree-gold hover:text-foreground"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Page
          </Button>
          <p className="text-xs text-muted-foreground/60">
            Tip: Press Ctrl+F5 for a hard refresh if the button doesn't work.
          </p>
        </div>
      </div>
    )
  }

  // Fallback for non-chunk errors
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-fintree-navy px-4 text-center">
      <div className="max-w-md space-y-6">
        <h1 className="text-2xl font-bold text-white">Something went wrong</h1>
        <p className="text-muted-foreground">
          {error instanceof Error ? error.message : 'An unexpected error occurred.'}
        </p>
        <Button
          onClick={() => window.location.reload()}
          className="bg-primary text-white hover:bg-fintree-gold hover:text-foreground"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh Page
        </Button>
      </div>
    </div>
  )
}
